/**
 * Phase 67-05 Wave 6 — module-level store for WS connection state.
 *
 * Allows polling-aware query hooks (useMachines, useStaleKiosks, etc.)
 * to read the WS fallback flag without importing useFleetWebSocket()
 * directly (which would require multiple hook mounts per page).
 *
 * Single source of truth: useFleetWebSocket() pushes state via
 * setWsState; everyone else subscribes via useWsConnectionState() which
 * is a useSyncExternalStore wrapper.
 *
 * Default state: { connected: false, fallbackMode: false } — when no
 * one has mounted the WS bridge yet, queries should poll at their
 * pre-67-05 cadence (i.e. fallback semantics).
 */

import { useSyncExternalStore } from "react";

export type WsConnectionState = {
  connected: boolean;
  fallbackMode: boolean;
  lastEventTs: number | null;
};

let state: WsConnectionState = {
  connected: false,
  fallbackMode: false,
  lastEventTs: null,
};

const listeners = new Set<() => void>();

export function setWsState(next: WsConnectionState): void {
  state = next;
  listeners.forEach((l) => l());
}

export function getWsState(): WsConnectionState {
  return state;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useWsConnectionState(): WsConnectionState {
  return useSyncExternalStore(subscribe, getWsState, getWsState);
}

/**
 * Returns the polling interval (ms) appropriate for the current WS
 * connection state. Used by useMachines / useStaleKiosks to keep their
 * existing polling cadence when the WS is down, and drop to a long
 * safety-net interval when the WS is healthy.
 */
export function pollingIntervalForWs(
  ws: WsConnectionState,
  options?: { activeMs?: number; fallbackMs?: number },
): number {
  const activeMs = options?.activeMs ?? 5 * 60 * 1000; // 5min safety net
  const fallbackMs = options?.fallbackMs ?? 30 * 1000; // legacy 30s polling
  return ws.connected && !ws.fallbackMode ? activeMs : fallbackMs;
}
